import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  input,
  output,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  OtpChallengeResult,
  OtpCreateChallengeRequest,
} from '@app/models/otp.model';
import { CommonUtilityService } from '@app/services/common-utility.service';
import { OtpService } from '@app/services/otp.service';
import { LinkSpacebarActivationDirective } from '@app/shared/directives/link-spacebar-activation/link-spacebar-activation.directive';
import { maskEmailAddress } from '@app/shared/utils/email.util';
import { extractHttpStatus } from '@app/shared/utils/http-error.util';
import {
  OTP_CHALLENGE_PURPOSE,
  OTP_CHALLENGE_STATUS,
  OTP_DELIVERY_CHANNEL,
  OTP_MASKED_EMAIL_PLACEHOLDER,
  TIME_FORMAT,
} from '@config/app-default.constant';
import { UdxSpinnerService } from '@upr-web-primer/udx-spinner';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-verify-your-identity-step-1',
  templateUrl: './verify-your-identity-step-1.component.html',
  styleUrl: './verify-your-identity-step-1.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [LinkSpacebarActivationDirective],
})
export class VerifyYourIdentityStep1Component implements OnInit {
  readonly email = input('');
  timer: number;
  next_resend_available_at: number;
  readonly guestId = input('');
  emailLinkUrl: string;
  pageHeading: string = '';
  readonly emailCodeSelected = output<OtpChallengeResult>();
  readonly limitReached = output<void>();
  readonly otpChallengeFailed = output<unknown>();

  pageLabels: Record<string, string> = {};

  /** Static fallback used when Tridion pageLabels resolver misses the description key. */
  private static readonly DEFAULT_DESCRIPTION =
    'We will send a verification code to <strong>%1$s</strong>.';
  isCreatingChallenge = false;

  get maskedEmailAddress(): string {
    return maskEmailAddress(this.email());
  }

  private readonly common = inject(CommonUtilityService);
  private readonly title = inject(Title);
  private readonly otpService = inject(OtpService);
  private readonly spinner = inject(UdxSpinnerService);
  readonly globalDefault = inject<any>('GlobalDefaults' as any);

  ngOnInit(): void {
    this.pageLabels = this.common.getResolvedPageData() ?? {};
    this.pageHeading =
      this.pageLabels?.['com.udx.account.pageinfo.stepone'] ||
      this.globalDefault.OTP_STEP1_PAGE_NAME;
    this.title.setTitle(
      this.pageLabels?.['com.udx.account.onetimepasscode.stepheader'] ||
        'Verify your identity',
    );
    this.common.scrollTop();
    this.common.triggerDDLEventHandler(
      this.pageLabels?.['com.udx.account.pageinfo.stepone'],
      {
        isPageLoad: true,
      },
    );
    this.common.setPageName(this.pageHeading);
    this.emailLinkUrl = window.location.href;
  }

  getFormattedDescription(): string {
    return this.formatLabelWithMaskedEmail(
      this.pageLabels?.['com.udx.account.onetimepasscode.description'] ||
        VerifyYourIdentityStep1Component.DEFAULT_DESCRIPTION,
    );
  }

  /**
   * Handles the email code event.
   * @returns {Promise<void>} A promise that resolves when the event is handled.
   */
  async onEmailCode(): Promise<void> {
    if (!this.email() || this.isCreatingChallenge) {
      return;
    }
    this.spinner.show();
    this.isCreatingChallenge = true;

    try {
      const request = this.buildCreateChallengeRequest();
      const challenge = await this.otpService.createChallenge(request);

      const challenge_response = await this.otpService.getChallenge(
        challenge.challengeId,
      );

      const currentTimeStamp = DateTime.now().toUTC().toISO();

      this.timer = this.getTimeDifference(
        currentTimeStamp,
        challenge_response.expiresAt,
      );
      this.next_resend_available_at = this.getTimeDifference(
        currentTimeStamp,
        challenge_response.resendAvailableAt,
      );
      const time = Math.trunc(this.timer);
      const next_resend_available_at = Math.trunc(
        this.next_resend_available_at,
      );
      const code_timer = {
        timer: time,
        challengeId: challenge.challengeId,
        resend_timer: next_resend_available_at,
        expiresAt: challenge_response.expiresAt,
      };

      this.common.setTimer(code_timer);

      if (challenge_response.status === OTP_CHALLENGE_STATUS.PENDING) {
        this.emailCodeSelected.emit(challenge);
      }
    } catch (error: unknown) {
      const status = extractHttpStatus(error);
      // 429 → rate-limited; skip banner and go straight to limit-reached view.
      if (status === 429) {
        if (error instanceof HttpErrorResponse) {
          this.common.triggerSystemErrorDDLEvent(error.error?.detail);
        }
        this.limitReached.emit();
        return;
      }
      this.otpChallengeFailed.emit(error);
    } finally {
      this.spinner.hide();
      this.isCreatingChallenge = false;
    }
  }

  private buildCreateChallengeRequest(): OtpCreateChallengeRequest {
    const guestId = this.guestId().trim();

    return {
      destination: this.email(),
      purpose: OTP_CHALLENGE_PURPOSE.EMAIL_VERIFICATION,
      channel: OTP_DELIVERY_CHANNEL.EMAIL,
      ...(guestId && { guest_id: guestId }),
    };
  }

  private formatLabelWithMaskedEmail(label = ''): string {
    return label.replace(OTP_MASKED_EMAIL_PLACEHOLDER, this.maskedEmailAddress);
  }

  getTimeDifference(startTime: any, endTime: any): number {
    const expirationTime = DateTime.fromISO(endTime);

    const difference = expirationTime.diff(
      DateTime.fromISO(startTime),
      TIME_FORMAT,
    );

    const seconds = difference.as('seconds');
    return seconds;
  }

  onSpacebarPress(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.preventDefault();
      this.onEmailCode();
    }
  }
}
